/**
 * https://stackoverflow.com/a/3368118
 * Draws a rounded rectangle using the current state of the canvas.
 * @param {CanvasRenderingContext2D} context
 * @param {Number} x The top left x coordinate
 * @param {Number} y The top left y coordinate
 * @param {Number} width The width of the rectangle
 * @param {Number} height The height of the rectangle
 * @param {Number} radius The corner radius
 */
export const roundRect = (
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
  strokeColor?: string,
) => {
  const maxRadius = Math.min(width, height) / 2;
  const isPerfectPill = radius >= maxRadius;
  
  context.beginPath();
  
  if (isPerfectPill) {
    // Perfect pill shape with semicircular caps
    if (width >= height) {
      // Wider than tall - horizontal pill with semicircular ends
      context.moveTo(x + radius, y);
      context.lineTo(x + width - radius, y);
      context.arc(x + width - radius, y + radius, radius, -Math.PI / 2, Math.PI / 2);
      context.lineTo(x + radius, y + height);
      context.arc(x + radius, y + radius, radius, Math.PI / 2, -Math.PI / 2);
    } else {
      // Taller than wide - vertical pill with semicircular ends
      context.moveTo(x, y + radius);
      context.arc(x + radius, y + radius, radius, Math.PI, 0);
      context.lineTo(x + width, y + height - radius);
      context.arc(x + radius, y + height - radius, radius, 0, Math.PI);
    }
  } else {
    // Regular rounded corners using quadratic curves
    context.moveTo(x + radius, y);
    context.lineTo(x + width - radius, y);
    context.quadraticCurveTo(x + width, y, x + width, y + radius);
    context.lineTo(x + width, y + height - radius);
    context.quadraticCurveTo(
      x + width,
      y + height,
      x + width - radius,
      y + height,
    );
    context.lineTo(x + radius, y + height);
    context.quadraticCurveTo(x, y + height, x, y + height - radius);
    context.lineTo(x, y + radius);
    context.quadraticCurveTo(x, y, x + radius, y);
  }
  
  context.closePath();
  context.fill();
  if (strokeColor) {
    context.strokeStyle = strokeColor;
  }
  context.stroke();
};

/**
 * Draws a rounded rectangle with individual corner radii using arcTo for perfect rounded corners.
 * Based on the algorithm provided for perfect corner rounding with overlap prevention.
 * @param {CanvasRenderingContext2D} context
 * @param {Number} x The top left x coordinate
 * @param {Number} y The top left y coordinate
 * @param {Number} width The width of the rectangle
 * @param {Number} height The height of the rectangle
 * @param {Object} radii Object with individual corner radii {tl, tr, br, bl}
 * @param {String} strokeColor Optional stroke color
 */
export const drawRoundedRectWithIndividualRadii = (
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radii: { tl: number; tr: number; br: number; bl: number },
  strokeColor?: string,
) => {
  // Extract individual corner radii (with defaults)
  let { tl, tr, br, bl } = radii; // top-left, top-right, bottom-right, bottom-left

  // **Clamp radii to avoid overlap (based on user's algorithm)**
  const maxX = Math.abs(width);
  const maxY = Math.abs(height);
  
  // Ensure no corner radius exceeds half the rectangle's dimensions
  tl = Math.min(tl, maxX / 2, maxY / 2);
  tr = Math.min(tr, maxX / 2, maxY / 2);
  br = Math.min(br, maxX / 2, maxY / 2);
  bl = Math.min(bl, maxX / 2, maxY / 2);

  // Ensure adjacent corners on each side do not overlap (sum <= side length)
  if (tl + tr > maxX) { 
    const scale = maxX / (tl + tr); 
    tl *= scale; 
    tr *= scale; 
  }
  if (bl + br > maxX) { 
    const scale = maxX / (bl + br); 
    bl *= scale; 
    br *= scale; 
  }
  if (tl + bl > maxY) { 
    const scale = maxY / (tl + bl); 
    tl *= scale; 
    bl *= scale; 
  }
  if (tr + br > maxY) { 
    const scale = maxY / (tr + br); 
    tr *= scale; 
    br *= scale; 
  }

  // Draw path using arcTo for perfect rounded corners
  context.beginPath();
  context.moveTo(x + tl, y); // move to top-left (offset by radius)
  context.lineTo(x + width - tr, y); // top horizontal line
  context.arcTo(x + width, y, x + width, y + tr, tr); // top-right corner arc
  context.lineTo(x + width, y + height - br); // right vertical line
  context.arcTo(x + width, y + height, x + width - br, y + height, br); // bottom-right arc
  context.lineTo(x + bl, y + height); // bottom horizontal line
  context.arcTo(x, y + height, x, y + height - bl, bl); // bottom-left arc
  context.lineTo(x, y + tl); // left vertical line
  context.arcTo(x, y, x + tl, y, tl); // top-left arc
  context.closePath();
  
  context.fill();
  if (strokeColor) {
    context.strokeStyle = strokeColor;
  }
  context.stroke();
};
